import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { MessageCircle, CheckCircle2, XCircle, Loader2, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const KakoDmTokenManager = ({ onTokenUpdated }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newToken, setNewToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get('/agency/dm-token/status');
      setStatus(response.data);
      if (response.data.configured && (!response.data.valid || response.data.expiring_soon)) {
        setExpanded(true);
      }
    } catch (error) {
      console.error('Erro ao verificar status do token de DM:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToken = async () => {
    if (!newToken.trim()) {
      toast.error('Cole o novo token antes de salvar');
      return;
    }
    setSaving(true);
    try {
      const response = await api.post('/agency/dm-token', { token: newToken.trim() });
      toast.success(response.data.message, { duration: 8000 });
      setNewToken('');
      setExpanded(false);
      await loadStatus();
      if (onTokenUpdated) onTokenUpdated();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao atualizar token de DM';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="border border-gray-200">
        <CardContent className="py-3 flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Verificando token de DM...
        </CardContent>
      </Card>
    );
  }

  const fallback = status?.using_fallback;
  const isValid = status?.valid;
  const borderCls = fallback
    ? 'border-amber-300 bg-amber-50/50'
    : isValid
      ? 'border-green-200 bg-green-50/50'
      : 'border-red-300 bg-red-50/50';

  return (
    <Card className={`border-2 ${borderCls}`} data-testid="kako-dm-token-manager">
      <CardContent className="py-3 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <MessageCircle className={`w-5 h-5 ${fallback ? 'text-amber-600' : isValid ? 'text-green-600' : 'text-red-600'}`} />
            <span className="font-semibold text-sm">Token de DM (Chatbot)</span>
            {fallback ? (
              <Badge className="bg-amber-500 text-white" data-testid="dm-token-status-badge">
                ⚠️ Usando token da agência
              </Badge>
            ) : isValid ? (
              <Badge className="bg-green-600 text-white" data-testid="dm-token-status-badge">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Válido
              </Badge>
            ) : (
              <Badge className="bg-red-600 text-white animate-pulse" data-testid="dm-token-status-badge">
                <XCircle className="w-3 h-3 mr-1" /> {status?.message || 'Expirado'}
              </Badge>
            )}
            {status?.nickname && (
              <span className="text-xs text-gray-500">
                ({status.nickname}{status?.show_id ? ` · ID ${status.show_id}` : ''})
              </span>
            )}
            {!fallback && isValid && status?.days_remaining != null && (
              status.expiring_soon ? (
                <Badge className="bg-amber-500 text-white animate-pulse" data-testid="dm-token-expiry-badge">
                  ⚠️ Expira em {status.days_remaining === 0 ? '< 1 dia' : `${status.days_remaining} dia${status.days_remaining === 1 ? '' : 's'}`}
                </Badge>
              ) : (
                <span className="text-xs text-gray-400" data-testid="dm-token-expiry-info">
                  expira em {status.days_remaining} dias
                </span>
              )
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={loadStatus} title="Reverificar" data-testid="dm-token-refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              data-testid="dm-token-toggle-btn"
            >
              {expanded ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
              {expanded ? 'Fechar' : status?.configured ? 'Substituir Token' : 'Configurar Token'}
            </Button>
          </div>
        </div>

        {expanded && (
          <div className="space-y-2 pt-2 border-t border-gray-200">
            <div className="text-xs text-gray-500 space-y-1">
              <p className="font-medium text-gray-700">
                Este token é da conta que ENVIA as DMs do chatbot (ex: <b>moedasonline.com</b>) — diferente do token da agência.
              </p>
              {status?.masked_token && (
                <p>Token atual: <code className="bg-gray-100 px-1 rounded">{status.masked_token}</code></p>
              )}
              {status?.updated_at && (
                <p>Última atualização: {new Date(status.updated_at).toLocaleString('pt-BR')}</p>
              )}
              {status?.expires_at && (
                <p>
                  Validade do token: <b>{new Date(status.expires_at).toLocaleDateString('pt-BR')}</b>
                  {status?.days_remaining != null && ` (${status.days_remaining} dia${status.days_remaining === 1 ? '' : 's'} restante${status.days_remaining === 1 ? '' : 's'})`}
                </p>
              )}
              <p className="text-orange-600 font-medium">
                Cole o token da conta de DM abaixo. Se a conta for diferente da atual, o estado do chatbot será reiniciado (conversas redescobertas automaticamente).
              </p>
            </div>
            <div className="flex gap-2 flex-col sm:flex-row">
              <Input
                type="text"
                placeholder="Cole o token da conta de DM aqui (eyJhbGciOi...)"
                value={newToken}
                onChange={(e) => setNewToken(e.target.value)}
                className="font-mono text-xs"
                data-testid="new-dm-token-input"
              />
              <Button
                onClick={handleSaveToken}
                disabled={saving || !newToken.trim()}
                className="bg-green-600 hover:bg-green-700 whitespace-nowrap"
                data-testid="save-dm-token-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Validando...</>
                ) : (
                  'Validar e Salvar'
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default KakoDmTokenManager;
